import { useDeferredValue, useMemo, useState } from 'react';
import { computeTextStats } from '../lib/textStats';
import { ToolHeader } from '../components/ToolHeader';

/**
 * `/tools/convert/text-stats` — chars / words / lines / sentences / UTF-8 bytes.
 *
 * Layout: a generous top textarea + a denser metrics card below. The
 * compute call is wrapped in `useDeferredValue` so the textarea stays
 * responsive while React re-renders the metrics list.
 */
export default function TextStatsTool(): JSX.Element {
  const [text, setText] = useState('');
  const deferred = useDeferredValue(text);
  const stats = useMemo(() => computeTextStats(deferred), [deferred]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="text-stats"
        category="convert"
        description="字符（codepoint）/ 单词 / 行 / 段落 / 句子 / UTF-8 字节 / 阅读时长。中文按 codepoint 计数，emoji 即使是 surrogate pair 也算 1 个字符。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">输入</header>
        <textarea
          className="input min-h-[180px] font-mono text-[13px]"
          placeholder="在此粘贴或输入文本…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
        />
      </section>

      <section className="card-pad">
        <header className="mb-2 text-[11px] uppercase tracking-wide text-ink-dim">统计</header>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="字符（含空格）" value={stats.chars.toLocaleString()} />
          <Stat label="字符（不含空格）" value={stats.charsNoSpace.toLocaleString()} />
          <Stat label="单词" value={stats.words.toLocaleString()} />
          <Stat label="行" value={stats.lines.toLocaleString()} />
          <Stat label="段落（空行分）" value={stats.paragraphs.toLocaleString()} />
          <Stat label="句子（。.! ?）" value={stats.sentences.toLocaleString()} />
          <Stat label="UTF-8 字节数" value={stats.utf8Bytes.toLocaleString()} />
          <Stat label="平均行长" value={`${stats.avgLineLength} 字符`} />
          <Stat
            label="最长行"
            value={
              stats.longestLine.lineNo === 0
                ? '-'
                : `${stats.longestLine.length} 字符（行 ${stats.longestLine.lineNo}）`
            }
          />
          <Stat label="阅读时间 (200 词/分)" value={`${stats.readingMinutes} 分钟`} />
        </dl>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line/30 py-1 sm:border-b-0">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="font-mono text-ink">{value}</dd>
    </div>
  );
}
